import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Prop,
  State,
  Listen,
  Watch
} from '@stencil/core';
import { TreeviewNodeType } from '../../models';
import { HTMLStencilElement } from '@stencil/core/internal';

@Component({
  tag: 'uui-tree-select-item',
  styleUrl: './uui-tree-select-item.css',
  shadow: false,
  scoped: true
})
export class UuiTreeSelect {
  @Prop({ mutable: true, reflect: false }) searchText: string;
  @Prop({ mutable: true }) item: TreeviewNodeType;
  @Prop() singleSelect?: boolean;
  @Prop() isTreeSelectCom: boolean = false;
  @Prop() groupName: string = '';
  @Event({ bubbles: true, composed: true, eventName: 'itemsUpdated' })
  itemUpdated: EventEmitter;
  @Event({ bubbles: true, composed: true, eventName: 'treeSelectExpand' })
  toggleExpand: EventEmitter;
  @Element() el!: HTMLStencilElement;
  @Prop() showChild: boolean = false;
  @State() currentItem: TreeviewNodeType;

  @Listen('itemToggle')
  itemToggle(event: CustomEvent<TreeviewNodeType>) {
    this.onItemUpdated(event.detail);
  }

  onItemsUpdated(event: CustomEvent<TreeviewNodeType>) {
    this.onItemUpdated(event.detail);
  }

  changeDisplaySettingOfChild() {
    this.showChild = !this.showChild;
    this.toggleExpand.emit({
      expanded: this.showChild,
      targetNode: this.item
    });
  }

  @Watch('searchText')
  itemsUpdated() {
    this.showChild = this.shouldExpandBasedOnSearch();
  }

  onItemUpdated(nodeChanged: TreeviewNodeType) {
    let itemToUpdate: TreeviewNodeType | null;

    // check if the node changed is the current item
    if (nodeChanged.id === this.item.id) {
      itemToUpdate = this.item;
      this.updateChildNodes(nodeChanged);
      this.item.checked = nodeChanged.checked;
    } else {
      const result = this.getItemFromTree(nodeChanged, this.item.children);
      if (result === null) return;
      itemToUpdate = result[0];
    }

    if (itemToUpdate.children?.length > 0) {
      if (itemToUpdate.children.every(child => child.checked)) {
        itemToUpdate.checked = true;
      } else {
        itemToUpdate.checked = false;
      }
    }

    this.itemUpdated.emit(itemToUpdate);
  }

  showChildItems(): boolean {
    return (
      this.item?.children?.length > 0 &&
      (this.showChild || this.searchText?.length > 0)
    );
  }

  shouldExpandBasedOnSearch(): boolean {
    return this.item?.children?.length > 0 && this.searchText?.length > 0;
  }

  childrenPartiallySelected(item: TreeviewNodeType): boolean {
    this.setIndeterminateStateForParentLevels(item);
    return item.indeterminate || false;
  }

  private updateChildNodes(updatedNode: TreeviewNodeType) {
    updatedNode.children?.forEach(childItem => {
      childItem.checked = updatedNode.checked;
      this.updateItem(updatedNode, childItem);
    });
  }

  private getItemFromTree(
    itemToFind: TreeviewNodeType,
    items: TreeviewNodeType[]
  ) {
    const getNodes = (result: TreeviewNodeType[], object: TreeviewNodeType) => {
      if (object.id === itemToFind.id) {
        result.push(object);
        return result;
      }
      if (Array.isArray(object.children)) {
        const nodes = object.children.reduce(getNodes, []);
        if (nodes.length) result.push({ ...object, children: nodes });
      }
      return result;
    };

    return items.reduce(getNodes, []);
  }

  private updateItem(
    updatedItem: TreeviewNodeType,
    itemToUpdate: TreeviewNodeType
  ) {
    itemToUpdate.checked = updatedItem.checked;
    itemToUpdate.children?.forEach(childNode => {
      childNode.checked = updatedItem.checked;
      this.updateItem(updatedItem, childNode);
    });
  }

  private setIndeterminateStateForParentLevels(item: TreeviewNodeType) {
    if (item.children) {
      item.children.forEach(child =>
        this.setIndeterminateStateForParentLevels(child)
      );
      const childChecked = item.children?.filter(
        child => child.checked || child.indeterminate
      ).length;
      item.indeterminate =
        childChecked > 0 && childChecked <= item.children.length;
    } else {
      item.indeterminate = false;
    }
  }

  render() {
    return (
      <Host>
        <div class="uui-tree-select-item-container">
          <div class="uui-tree-select-child-container">
            <div class="uui-tree-select-child-toggle-icon-container">
              {(this.item &&
                this.item.children &&
                this.item.children.length > 0) ||
              this.showChildItems() ? (
                <div>
                  <button
                    onClick={this.changeDisplaySettingOfChild.bind(this)}
                    class={
                      this.showChild
                        ? 'uui-button-toggle-expand uui-button-toggle-isexpanded'
                        : 'uui-button-toggle-expand'
                    }
                  >
                    <uui-icon type={'angleRight'} size="lg" />
                  </button>
                </div>
              ) : (
                <div></div>
              )}
            </div>
            {this.item.hideCheckbox ? (
              <p style={{ marginBottom: '14px' }}>{this.item.label}</p>
            ) : (
              <div class="uui-tree-select-child-item-container">
                {this.singleSelect ? (
                  <uui-tree-select-radiobutton
                    item={this.item}
                    groupName={this.groupName}
                    indeterminate={this.childrenPartiallySelected(this.item)}
                  ></uui-tree-select-radiobutton>
                ) : (
                  <uui-tree-select-checkbox
                    item={this.item}
                    groupName={this.groupName}
                    indeterminate={this.childrenPartiallySelected(this.item)}
                  ></uui-tree-select-checkbox>
                )}
              </div>
            )}
          </div>
          <div>
            <p class="uui-extra-small uui-tree-select-item-count">
              {this.item.count}
            </p>
          </div>
        </div>
        {this.showChild && this.showChildItems() ? (
          <div class="uui-tree-select-indent-container">
            {this.item.children.map(item => {
              return (
                <div class="child-indent">
                  <uui-tree-select-item
                    searchText={this.searchText}
                    item={item}
                    singleSelect={this.singleSelect}
                    isTreeSelectCom={true}
                    class="item-section"
                  ></uui-tree-select-item>
                </div>
              );
            })}
          </div>
        ) : null}
      </Host>
    );
  }
}
